import React, { useEffect, useState } from "react";
import { Award, Download, Star, Send, Loader2, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudentGraduate() {
  const { toast } = useToast();
  const [certs, setCerts] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/portal/student/certificates").then((r) => setCerts(r.data.certificates || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!text.trim()) { toast({ title: "Please write your testimonial", variant: "destructive" }); return; }
    setSending(true);
    try {
      await api.post("/portal/student/testimonial", { rating, text });
      toast({ title: "Thank you! 🙏", description: "Your testimonial was submitted for review." });
      setSent(true); setText("");
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-xl font-bold text-[#012a1e] flex items-center gap-2"><GraduationCap size={20} className="text-[#c9a227]" /> My Certificates</h2>
        <p className="text-slate-500 text-sm mt-1">Download or share your GDIH-verified certificates. Each has a QR code anyone can scan to verify.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {certs.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`grad-cert-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#004030] text-[#e0c05a] grid place-items-center"><Award size={20} /></div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.status === "Valid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{c.status || "Valid"}</span>
              </div>
              <p className="font-semibold text-[#012a1e] mt-3">{c.programme}</p>
              <p className="text-xs text-slate-500">{c.institution || ""}{c.grade ? ` · ${c.grade}` : ""}</p>
              <p className="text-xs font-mono text-slate-400 mt-1">{c.certificate_no}</p>
              <a href={`${BACKEND}/api/public/certificate-pdf?q=${encodeURIComponent(c.certificate_no)}`} target="_blank" rel="noreferrer" data-testid={`download-cert-${c.id}`}>
                <Button className="mt-3 w-full bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-10 text-sm"><Download size={15} className="mr-1" /> Download PDF</Button>
              </a>
            </div>
          ))}
          {!certs.length && (
            <div className="sm:col-span-2 bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-400">
              No certificates issued yet. They'll appear here once your programme is complete.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="font-display text-xl font-bold text-[#012a1e] flex items-center gap-2"><Star size={20} className="text-[#c9a227]" /> Share your story</h2>
        <p className="text-slate-500 text-sm mt-1">Loved your GDIH journey? Leave a testimonial — approved reviews appear on our website.</p>
        {sent ? (
          <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 rounded-xl p-3" data-testid="testimonial-sent">Thanks! Your testimonial is awaiting review. 💚</p>
        ) : (
          <>
            <div className="flex items-center gap-1 mt-4" data-testid="testimonial-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} type="button" className="p-0.5">
                  <Star size={26} className={n <= rating ? "fill-[#c9a227] text-[#c9a227]" : "text-slate-300"} />
                </button>
              ))}
            </div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Tell future students about your experience with GDIH…" className="mt-3 min-h-24" data-testid="testimonial-text" />
            <Button onClick={submit} disabled={sending} data-testid="testimonial-submit" className="mt-3 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <><Send size={15} className="mr-1" /> Submit Testimonial</>}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
